import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import GiftEditor from "components/admin/GiftEditor";
import { useFetch } from "components/admin/hooks";
import api from "api/client";
import { ItemName } from "./ItemsEditor";
import { boardName } from "./shared";

/** Mã lựa chọn NPC cho công thức mới. Dải riêng để không trùng mã menu có sẵn của game. */
function newOption(recipes) {
  const used = new Set(recipes.map((r) => Number(r.option)));
  let option = 700000 + Math.floor(Math.random() * 90000);
  while (used.has(option)) option += 1;
  return option;
}

function RoleSelect({ label, value, roles, items, onChange, disabled }) {
  return (
    <MDInput
      select
      fullWidth
      label={label}
      value={value || ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      {roles.map((role) => (
        <MenuItem key={role} value={role}>
          <ItemName itemId={items[role]} />
        </MenuItem>
      ))}
    </MDInput>
  );
}

RoleSelect.defaultProps = { value: "", disabled: false };
RoleSelect.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  roles: PropTypes.arrayOf(PropTypes.string).isRequired,
  items: PropTypes.shape({}).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

function Card({ children, onRemove, locked }) {
  return (
    <MDBox
      p={2}
      borderRadius="lg"
      position="relative"
      sx={{ border: "1px solid", borderColor: "divider" }}
    >
      {!locked && onRemove && (
        <IconButton size="small" onClick={onRemove} sx={{ position: "absolute", top: 4, right: 4 }}>
          <Icon fontSize="small">delete</Icon>
        </IconButton>
      )}
      {children}
    </MDBox>
  );
}

Card.defaultProps = { onRemove: null };
Card.propTypes = {
  children: PropTypes.node.isRequired,
  onRemove: PropTypes.func,
  locked: PropTypes.bool.isRequired,
};

/** Khối farm và chế (modules.farm_craft): công thức, công dụng khi dùng, tỉ lệ rơi. */
function CraftEditor({ config, locked, update }) {
  const maps = useFetch(() => api.get("/api/admin/templates/maps"), []);
  const craft = config.modules?.farm_craft;
  const items = config.items || {};
  const roles = Object.keys(items).filter((role) => Number(items[role]) > 0);
  const boards = config.boards || [];
  const coinKeys = Object.keys(config.texts || {}).filter((key) => key.startsWith("coin_"));

  if (!craft) {
    return (
      <Alert severity="info">
        Loại event này không có chế đồ. Mở tab JSON nếu cần thêm khối farm_craft.
      </Alert>
    );
  }

  const setCraft = (fn) =>
    update((next) => {
      fn(next.modules.farm_craft);
    });
  const recipes = craft.recipes || [];
  const uses = craft.use || {};
  const drop = craft.drop || {};
  const dropMaps = Object.entries(drop.maps || {});
  const mapName = (key) => {
    const map = (maps.data || []).find((m) => String(m.mapId) === String(key));
    return map ? `${map.name} (#${map.mapId})` : key;
  };

  return (
    <MDBox display="flex" flexDirection="column" gap={2}>
      {locked && (
        <Alert severity="info">Mùa đã bắt đầu nên công thức, công dụng, tỉ lệ rơi chỉ xem.</Alert>
      )}

      <MDTypography variant="h6">Công thức chế ở NPC</MDTypography>
      {recipes.map((recipe, index) => {
        const need = Object.entries(recipe.need || {});
        const setRecipe = (patch) =>
          setCraft((c) => {
            c.recipes[index] = { ...c.recipes[index], ...patch };
          });
        return (
          <Card
            key={recipe.option}
            locked={locked}
            onRemove={() =>
              setCraft((c) => {
                c.recipes.splice(index, 1);
              })
            }
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <MDInput
                  fullWidth
                  label="Tên nút trên NPC"
                  value={recipe.name || ""}
                  disabled={locked}
                  onChange={(e) => setRecipe({ name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <RoleSelect
                  label="Chế ra"
                  value={recipe.out}
                  roles={roles}
                  items={items}
                  disabled={locked}
                  onChange={(out) => setRecipe({ out })}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <MDInput
                  fullWidth
                  type="number"
                  label="Tốn vàng"
                  value={recipe.gold ?? 0}
                  disabled={locked}
                  inputProps={{ min: 0 }}
                  onChange={(e) => setRecipe({ gold: Number(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <MDInput
                  fullWidth
                  type="number"
                  label="Tốn ngọc"
                  value={recipe.coin ?? 0}
                  disabled={locked}
                  inputProps={{ min: 0 }}
                  onChange={(e) => setRecipe({ coin: Number(e.target.value) || 0 })}
                />
              </Grid>
            </Grid>
            <MDTypography variant="caption" fontWeight="medium" display="block" mt={2} mb={1}>
              Nguyên liệu
            </MDTypography>
            {need.map(([role, count]) => (
              <MDBox key={role} display="flex" gap={1} alignItems="center" mb={1}>
                <MDBox flex={1}>
                  <RoleSelect
                    label="Vật phẩm"
                    value={role}
                    roles={roles.filter((r) => r === role || !(r in recipe.need))}
                    items={items}
                    disabled={locked}
                    onChange={(newRole) =>
                      setRecipe({
                        need: Object.fromEntries(
                          need.map(([r, c]) => (r === role ? [newRole, c] : [r, c]))
                        ),
                      })
                    }
                  />
                </MDBox>
                <MDInput
                  type="number"
                  label="Số lượng"
                  value={count}
                  disabled={locked}
                  sx={{ width: 120 }}
                  inputProps={{ min: 1 }}
                  onChange={(e) =>
                    setRecipe({
                      need: { ...recipe.need, [role]: Math.max(1, Number(e.target.value) || 1) },
                    })
                  }
                />
                {!locked && (
                  <IconButton
                    size="small"
                    onClick={() =>
                      setRecipe({ need: Object.fromEntries(need.filter(([r]) => r !== role)) })
                    }
                  >
                    <Icon fontSize="small">close</Icon>
                  </IconButton>
                )}
              </MDBox>
            ))}
            {!locked && roles.some((r) => !(r in (recipe.need || {}))) && (
              <MDButton
                size="small"
                variant="text"
                color="info"
                onClick={() => {
                  const role = roles.find((r) => !(r in (recipe.need || {})) && r !== recipe.out);
                  if (role) setRecipe({ need: { ...(recipe.need || {}), [role]: 1 } });
                }}
              >
                + Thêm nguyên liệu
              </MDButton>
            )}
          </Card>
        );
      })}
      {!locked && (
        <MDBox>
          <MDButton
            size="small"
            variant="outlined"
            color="info"
            disabled={roles.length === 0}
            onClick={() =>
              setCraft((c) => {
                c.recipes = [
                  ...(c.recipes || []),
                  {
                    option: newOption(c.recipes || []),
                    name: "Chế mới",
                    out: roles[0],
                    need: {},
                    gold: 0,
                    coin: 0,
                  },
                ];
              })
            }
          >
            + Thêm công thức
          </MDButton>
        </MDBox>
      )}

      <Divider />
      <MDTypography variant="h6">Khi người chơi dùng vật phẩm</MDTypography>
      {Object.entries(uses).map(([role, use]) => {
        const setUse = (patch) =>
          setCraft((c) => {
            c.use[role] = { ...c.use[role], ...patch };
          });
        return (
          <Card
            key={role}
            locked={locked}
            onRemove={() =>
              setCraft((c) => {
                delete c.use[role];
              })
            }
          >
            <MDTypography variant="button" fontWeight="medium" display="block" mb={1.5}>
              Dùng <ItemName itemId={items[role]} />
            </MDTypography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={2}>
                <MDInput
                  fullWidth
                  type="number"
                  label="Điểm mỗi lần"
                  value={use.value ?? 1}
                  disabled={locked}
                  inputProps={{ min: 0 }}
                  onChange={(e) => setUse({ value: Number(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <MDInput
                  select
                  fullWidth
                  label="Cộng vào bảng"
                  value={use.score || ""}
                  disabled={locked}
                  onChange={(e) => setUse({ score: e.target.value })}
                >
                  <MenuItem value="">Không cộng bảng</MenuItem>
                  {boards.map((b) => (
                    <MenuItem key={b.key} value={b.key}>
                      {boardName(config, b.key)}
                    </MenuItem>
                  ))}
                </MDInput>
              </Grid>
              <Grid item xs={6} md={3}>
                <MDInput
                  select
                  fullWidth
                  label="Cộng tiền đổi quà"
                  value={use.coin || ""}
                  disabled={locked}
                  onChange={(e) => setUse({ coin: e.target.value || undefined })}
                >
                  <MenuItem value="">Không</MenuItem>
                  {coinKeys.map((key) => (
                    <MenuItem key={key} value={key}>
                      {config.texts[key] || key}
                    </MenuItem>
                  ))}
                </MDInput>
              </Grid>
              <Grid item xs={6} md={4}>
                <MDInput
                  fullWidth
                  label="Thông báo khi dùng"
                  value={use.text || ""}
                  disabled={locked}
                  onChange={(e) => setUse({ text: e.target.value })}
                />
              </Grid>
            </Grid>
            <MDBox mt={2}>
              {use.gift || !locked ? (
                <GiftEditor
                  value={use.gift || []}
                  onChange={(gift) => setUse({ gift: gift.length ? gift : undefined })}
                  label="Quà khi mở (để trống nếu chỉ cộng điểm)"
                />
              ) : null}
            </MDBox>
          </Card>
        );
      })}
      {!locked && roles.some((r) => !(r in uses)) && (
        <MDBox maxWidth={360}>
          <RoleSelect
            label="+ Cho phép dùng thêm vật phẩm"
            roles={roles.filter((r) => !(r in uses))}
            items={items}
            onChange={(role) =>
              setCraft((c) => {
                c.use = { ...(c.use || {}), [role]: { value: 1, text: "Đã dùng" } };
              })
            }
          />
        </MDBox>
      )}

      {craft.drop && (
        <>
          <Divider />
          <MDTypography variant="h6">Rơi nguyên liệu khi đánh quái</MDTypography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <MDInput
                fullWidth
                type="number"
                label="Tỉ lệ rơi mặc định (%)"
                value={drop.default ?? 0}
                disabled={locked}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                helperText="Áp cho mọi map không có trong danh sách dưới"
                onChange={(e) =>
                  setCraft((c) => {
                    c.drop.default = Number(e.target.value) || 0;
                  })
                }
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <MDInput
                fullWidth
                type="number"
                label="Tối đa mỗi người mỗi ngày"
                value={craft.daily_drop_cap ?? 0}
                disabled={locked}
                inputProps={{ min: 0 }}
                helperText="0 = không giới hạn"
                onChange={(e) =>
                  setCraft((c) => {
                    c.daily_drop_cap = Number(e.target.value) || 0;
                  })
                }
              />
            </Grid>
          </Grid>
          {dropMaps.map(([key, percent]) => (
            <MDBox key={key} display="flex" gap={1} alignItems="center">
              <MDTypography variant="button" sx={{ minWidth: 240 }}>
                {mapName(key)}
              </MDTypography>
              <MDInput
                type="number"
                label="%"
                value={percent}
                disabled={locked}
                sx={{ width: 120 }}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                onChange={(e) =>
                  setCraft((c) => {
                    c.drop.maps[key] = Number(e.target.value) || 0;
                  })
                }
              />
              {!locked && (
                <IconButton
                  size="small"
                  onClick={() =>
                    setCraft((c) => {
                      delete c.drop.maps[key];
                    })
                  }
                >
                  <Icon fontSize="small">close</Icon>
                </IconButton>
              )}
            </MDBox>
          ))}
          {!locked && (
            <MDBox maxWidth={360}>
              <MDInput
                select
                fullWidth
                label="+ Đặt tỉ lệ riêng cho map"
                value=""
                onChange={(e) =>
                  setCraft((c) => {
                    c.drop.maps = {
                      ...(c.drop.maps || {}),
                      [String(e.target.value)]: c.drop.default ?? 0,
                    };
                  })
                }
              >
                {(maps.data || [])
                  .filter((m) => !dropMaps.some(([key]) => String(key) === String(m.mapId)))
                  .map((m) => (
                    <MenuItem key={m.mapId} value={m.mapId}>
                      {m.name} (#{m.mapId})
                    </MenuItem>
                  ))}
              </MDInput>
            </MDBox>
          )}
        </>
      )}
    </MDBox>
  );
}

CraftEditor.propTypes = {
  config: PropTypes.shape({
    modules: PropTypes.shape({ farm_craft: PropTypes.shape({}) }),
    items: PropTypes.shape({}),
    boards: PropTypes.arrayOf(PropTypes.shape({})),
    texts: PropTypes.shape({}),
  }).isRequired,
  locked: PropTypes.bool.isRequired,
  update: PropTypes.func.isRequired,
};

export default CraftEditor;
